import { useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Lock } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';

/**
 * Reusable "you need to be logged in" prompt.
 *
 * Shown whenever a guest taps a locked control. Sends them to /login or
 * /register with the current path as the post-auth redirect so they land
 * right back where they were, ready to actually use the feature.
 *
 * Props:
 *   open      - controlled visibility
 *   onClose   - called when user dismisses (X, backdrop, escape)
 *   feature   - optional short feature name shown in the body, e.g.
 *               "Civilian Mode" / "the + Add button" / "Bookings".
 *               Falls back to the generic "all app features" pitch.
 *   description - optional override of the body copy (defaults to the
 *               generic "Register / log in to use all app features").
 *   title       - optional override of the headline.
 */
export default function AuthGateDialog({ open, onClose, feature, description, title }) {
  const navigate = useNavigate();
  const location = useLocation();

  const go = (path) => {
    // Honour both `?next=` (the convention used by LoginPage and
    // RegisterPage) and the absolute current path so post-auth lands
    // the user exactly where they started.
    const next = encodeURIComponent(location.pathname + location.search);
    navigate(`${path}?next=${next}`);
    onClose?.();
  };

  const headline = title || (feature
    ? `Sign up to use ${feature}`
    : 'Want the full RollM8s?');

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-sm" data-testid="auth-gate-dialog">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 rounded-2xl bg-fuchsia-500/15 border border-fuchsia-500/30 flex items-center justify-center mb-2">
            <Lock className="w-5 h-5 text-fuchsia-300" />
          </div>
          <DialogTitle className="text-center text-lg font-bold">
            {headline}
          </DialogTitle>
          <DialogDescription className="text-center text-sm">
            {description || 'Register for free or log in to use all app features.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 mt-2">
          <Button
            onClick={() => go('/register')}
            className="w-full"
            data-testid="auth-gate-register-btn"
          >
            Register for free
          </Button>
          <Button
            variant="outline"
            onClick={() => go('/login')}
            className="w-full"
            data-testid="auth-gate-login-btn"
          >
            Log in
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Wraps a section of UI that should be visible-but-locked when the
 * current visitor isn't logged in. While `locked`:
 *   • children are rendered at 60% opacity (still readable & visible
 *     enough to communicate "this is what the app does")
 *   • a transparent overlay catches every click and opens the
 *     AuthGateDialog so guests get a single, consistent prompt
 *     instead of nested clickable elements firing off random
 *     navigations.
 *
 * When `locked={false}` the wrapper passes children through as-is —
 * zero behaviour change for logged-in users.
 */
export function GuestFade({ locked, feature, children, className = '' }) {
  const [open, setOpen] = useState(false);
  if (!locked) return children;
  return (
    <>
      <div className={`relative ${className}`}>
        {/* Locked sections sit visibly darker than active ones so the
            unlocked tiles pop and locked ones clearly read as "not
            available yet". */}
        <div className="opacity-30 grayscale pointer-events-none select-none">
          {children}
        </div>
        {/* Click-eater overlay → opens the auth gate prompt */}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
          className="absolute inset-0 w-full h-full cursor-pointer"
          aria-label={feature ? `Sign up to use ${feature}` : 'Sign up to use this feature'}
          data-testid={`guest-fade-${(feature || 'section').toLowerCase().replace(/\s+/g, '-')}`}
        />
      </div>
      <AuthGateDialog open={open} onClose={() => setOpen(false)} feature={feature} />
    </>
  );
}
